import React, { FC, useState, useEffect, useMemo, useRef } from 'react'
import Image from 'next/image'
import moment, { Moment } from 'moment'
import { useTranslation } from 'next-i18next'

import { Calendar } from 'components/columns/LockerBookingDatePicker/Calendar'
import { TimePicker } from './Time'

import { getSpaceBookingCalendarMaxMonths } from 'features/space/lib/getSpaceBookingCalendarMaxMonths'
import { Space } from 'state/ducks/space/models.new'

import styles from './styles.module.css'

type Props = {
  startDate: Moment
  endDate: Moment
  startTime: string
  endTime: string
  space: Space
  timeRange: {
    checkin: {
      minTime: string
      maxTime: string
    }
    checkout: {
      minTime: string
      maxTime: string
    }
  }
  timePickerError: string
  setStartDate: (date: Moment) => void
  setEndDate: (date: Moment) => void
  setStartTime: (time: string) => void
  setEndTime: (time: string) => void
}

export const SpaceBookingDatePicker: FC<Props> = ({
  startDate,
  endDate,
  startTime,
  endTime,
  space,
  timeRange,
  timePickerError,
  setStartDate,
  setEndDate,
  setStartTime,
  setEndTime,
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>()

  const [showPicker, setShowPicker] = useState<boolean>(false)
  const maxDate = useMemo(
    () => moment().add(getSpaceBookingCalendarMaxMonths(space.space_type), 'month').endOf('day'),
    [space.space_type]
  )

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowPicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.label}>{t('SPACE_DETAIL.CHECKIN_OUT_LABEL')}</div>
      <div ref={ref} className={styles.dateContainer}>
        <div onClick={() => setShowPicker(!showPicker)} className={styles.date}>
          {!!startDate ? (
            <div className={styles.selectedDate}>
              {moment(startDate).format('YYYY/MM/DD')} -{' '}
              {moment(endDate || startDate).format('YYYY/MM/DD')}
            </div>
          ) : (
            <div className={styles.placeholder}>{t('SPACE_DETAIL.SELECT_DATE_PLACEHOLDER')}</div>
          )}

          <Image src="/calendar.png" height={24} width={24} alt="calendar" />
        </div>

        <div className={styles.calendarContainer}>
          <Calendar
            locale="ja"
            isOpen={showPicker}
            selectedDate={{
              startDate: startDate ? moment(startDate) : null,
              endDate: endDate ? moment(endDate) : null,
            }}
            bookingStatus={space.availabilities}
            maxDate={maxDate}
            setSelectedDate={({ startDate, endDate }) => {
              setStartDate(startDate)
              setEndDate(endDate)

              setStartTime('')
              setEndTime('')
            }}
            onChangeMonth={() => {}}
          />
        </div>
      </div>

      <div className={styles.timePickerContainer}>
        <div className={styles.startTimeContainer}>
          <TimePicker
            label={t('SPACE_DETAIL.CHECKIN_TIME_LABEL')}
            time={startTime}
            minTime={timeRange?.checkin.minTime || '00:00'}
            maxTime={timeRange?.checkin.maxTime || '23:30'}
            onSetTime={setStartTime}
            disabled={!timeRange}
          />
        </div>
        <div className={styles.endTimeContainer}>
          <TimePicker
            label={t('SPACE_DETAIL.CHECKOUT_TIME_LABEL')}
            time={endTime}
            minTime={timeRange?.checkout.minTime || '00:00'}
            maxTime={timeRange?.checkout.maxTime || '23:30'}
            onSetTime={setEndTime}
            disabled={!timeRange}
          />
        </div>
      </div>
      {!!timePickerError && <div className={styles.error}>{timePickerError}</div>}
    </div>
  )
}
